import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useAppStore } from '../../../store';
import { appRegistry } from '../appRegistry';
import { AppCard } from './AppCard';
import type { App } from '../../../types/app.types';

export const AppGrid = () => {
  const navigate = useNavigate();
  const { apps, setApps, reorderApps, setSelectedApp } = useAppStore();
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    // Initialize apps from registry
    setApps([...appRegistry].sort((a, b) => a.order - b.order));
  }, [setApps]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = apps.findIndex((app) => app.id === active.id);
      const newIndex = apps.findIndex((app) => app.id === over.id);

      const newApps = arrayMove(apps, oldIndex, newIndex).map((app, index) => ({
        ...app,
        order: index,
      }));

      reorderApps(newApps);
    }
  };

  const handleAppClick = (app: App) => {
    setSelectedApp(app);
    console.log('App clicked:', app);
    
    // Navigate to app URL if it exists
    if (app.url) {
      navigate(app.url);
    }
    // In a real application, this would open the app in a modal or navigate to it
  };

  const filteredApps = filter === 'all' ? apps : apps.filter((app) => app.category === filter);

  return (
    <div className="space-y-6">
      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-primary text-white'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilter('game')}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            filter === 'game'
              ? 'bg-primary text-white'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Games
        </button>
        <button
          onClick={() => setFilter('app')}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            filter === 'app'
              ? 'bg-primary text-white'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Apps
        </button>
        <button
          onClick={() => setFilter('tool')}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            filter === 'tool'
              ? 'bg-primary text-white'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Tools
        </button>
      </div>

      {/* App Grid with Drag & Drop */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={filteredApps.map((app) => app.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filteredApps.map((app) => (
              <AppCard key={app.id} app={app} onClick={handleAppClick} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {filteredApps.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          No apps found in this category.
        </div>
      )}
    </div>
  );
};
